import React, { useEffect, useMemo, useState } from 'react';
import client from '../api/client';
import { useToast } from '../context/ToastContext';
import { usePermissions, PAGE_PERMISSION_IDS } from '../hooks/usePermissions';
import { applyScheduleOrder } from '../utils/scheduleOrderStorage';
import {
  saveSelectedCity,
  getSelectedCity,
  getPageFilters,
  savePageFilters,
} from '../utils/userPreferences';
import ProjectOfficeCitySelect from '../components/ProjectOfficeCitySelect';
import ProjectOfficeFilterMultiSelect from '../components/ProjectOfficeFilterMultiSelect';
import SyncedHorizontalScroll from '../components/SyncedHorizontalScroll';
import {
  EMPTY_STATUS,
  collectResponsibleOptions,
  formatDateForInput,
  formatSortValue,
  normalizeCityIds,
  parseSortValue,
  parseTaskDateMs,
  taskMatchesDateSearch,
  taskMatchesResponsibleFilter,
  taskMatchesSetDateRange,
  taskMatchesStatusFilter,
} from '../utils/projectOfficeFilters';

const STATUS_COLORS = {
  'Отложено': '#007bff',
  'В работе': '#ffc107',
  'Не актуально': '#dc3545',
  'Выполнено': '#28a745'
};

const PO_PREFS_KEY = 'project_office';

const persistPoSelection = (cityIds, includeCommon) => {
  const normalized = normalizeCityIds(cityIds);
  savePageFilters(PO_PREFS_KEY, {
    cityIds: normalized,
    includeCommon: !!includeCommon,
  });
  if (normalized.length > 0) saveSelectedCity(normalized[0]);
  else saveSelectedCity(null);
};

const ProjectOffice = () => {
  const { canEdit } = usePermissions();
  const projectOfficeCanEdit = canEdit(PAGE_PERMISSION_IDS.project_office);
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState(new Set());
  const [includeCommon, setIncludeCommon] = useState(false);
  const [viewTab, setViewTab] = useState('active'); // active | archive
  const [tasks, setTasks] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [setDateFrom, setSetDateFrom] = useState('');
  const [setDateTo, setSetDateTo] = useState('');
  const [responsibleFilter, setResponsibleFilter] = useState(() => new Set());
  const [statusFilter, setStatusFilter] = useState(() => new Set());
  const [dateSearch, setDateSearch] = useState('');
  const [sortBy, setSortBy] = useState('set_date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [expandedComments, setExpandedComments] = useState({});
  const { showSuccess, showError, showInfo } = useToast();

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    if (selectedCities.size > 0 || includeCommon) {
      fetchTasks();
    } else {
      setTasks([]);
    }
  }, [selectedCities, includeCommon, viewTab]);

  const fetchCities = async () => {
    try {
      const res = await client.get('/cities');
      const visibleCities = (res.data || []).filter(
        (city) => city.visible_in_schedules !== false
      );
      setCities(visibleCities);

      const prefs = getPageFilters(PO_PREFS_KEY);
      const visibleIds = new Set(visibleCities.map((c) => Number(c.id)));
      let nextIds = [];
      if (Array.isArray(prefs.cityIds) && prefs.cityIds.length) {
        nextIds = normalizeCityIds(prefs.cityIds).filter((id) => visibleIds.has(id));
      } else {
        const savedCity = getSelectedCity();
        const savedNum = savedCity != null ? Number(savedCity) : null;
        if (savedNum != null && Number.isFinite(savedNum) && visibleIds.has(savedNum)) {
          nextIds = [savedNum];
        }
      }

      const nextCommon = !!prefs.includeCommon;
      setSelectedCities(new Set(nextIds));
      setIncludeCommon(nextCommon);
      persistPoSelection(nextIds, nextCommon);
    } catch (e) {
      console.error(e);
      showError('Ошибка при загрузке объектов');
    }
  };

  const handleChangeCities = (nextSet) => {
    setSelectedCities(nextSet);
    persistPoSelection([...nextSet], includeCommon);
  };

  const handleToggleCommon = () => {
    const next = !includeCommon;
    setIncludeCommon(next);
    persistPoSelection([...selectedCities], next);
  };

  const fetchTasks = async () => {
    try {
      const params = { view: viewTab };
      if (selectedCities.size > 0) params.city_ids = [...selectedCities].join(',');
      if (includeCommon) params.include_common = true;
      if (!params.city_ids && !params.include_common) return;
      const res = await client.get('/project-office', { params });
      const firstCity = selectedCities.size > 0 ? [...selectedCities][0] : null;
      const ordered = firstCity ? applyScheduleOrder(res.data, firstCity, 'project_office') : res.data;
      setTasks(ordered);
    } catch (e) {
      console.error(e);
      showError('Ошибка при загрузке задач');
    }
  };

  useEffect(() => {
    const adjust = () => {
      const areas = document.querySelectorAll('textarea[data-auto-resize="true"]');
      areas.forEach((el) => {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      });
    };
    const id = setTimeout(adjust, 0);
    return () => clearTimeout(id);
  }, [tasks, expandedComments]);

  const responsibleOptions = useMemo(() => collectResponsibleOptions(tasks), [tasks]);

  const statusOptions = useMemo(() => ([
    ...Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s })),
    { value: EMPTY_STATUS, label: 'Без статуса' },
  ]), []);

  const resetFilters = () => {
    setSearchText('');
    setSetDateFrom('');
    setSetDateTo('');
    setResponsibleFilter(new Set());
    setStatusFilter(new Set());
    setDateSearch('');
  };

  const filteredTasks = useMemo(() => {
    const q = (searchText || '').toLowerCase().trim();
    let list = q ? tasks.filter(t => (
      (t.task || '').toLowerCase().includes(q) ||
      (t.responsible || '').toLowerCase().includes(q) ||
      (t.participants || '').toLowerCase().includes(q) ||
      (t.comments || '').toLowerCase().includes(q) ||
      (t.initiator || '').toLowerCase().includes(q) ||
      (t.delay_reason || '').toLowerCase().includes(q) ||
      (t.result || '').toLowerCase().includes(q)
    )) : [...tasks];

    list = list.filter((t) => taskMatchesSetDateRange(t, setDateFrom, setDateTo));
    list = list.filter((t) => taskMatchesResponsibleFilter(t, responsibleFilter));
    list = list.filter((t) => taskMatchesStatusFilter(t, statusFilter));
    list = list.filter((t) => taskMatchesDateSearch(t, dateSearch));

    const aVal = (t) => {
      switch (sortBy) {
        case 'initiator': return (t.initiator || '').toLowerCase();
        case 'responsible': return (t.responsible || '').toLowerCase();
        case 'due_date': return (t.due_date || '').toLowerCase();
        case 'status': return (t.status || '').toLowerCase();
        case 'set_date': return parseTaskDateMs(t.set_date) ?? 0;
        default: return 0;
      }
    };

    list.sort((a, b) => {
      const va = aVal(a);
      const vb = aVal(b);
      let cmp = 0;
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), 'ru');
      }
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [
    tasks,
    searchText,
    setDateFrom,
    setDateTo,
    responsibleFilter,
    statusFilter,
    dateSearch,
    sortBy,
    sortOrder,
  ]);

  const getStatusCellStyle = (status) => {
    if (!status) return {};
    const color = STATUS_COLORS[status];
    if (!color) return {};
    return {
      backgroundColor: `${color}22`,
      borderLeft: `3px solid ${color}`
    };
  };

  const addRow = () => {
    if (!includeCommon && selectedCities.size === 0) {
      showInfo('Выберите объект(ы) или «Общие задачи»');
      return;
    }
    if (selectedCities.size > 1) {
      showInfo('Для добавления строки оставьте выбранным только один объект');
      return;
    }
    const cityId = selectedCities.size > 0 ? [...selectedCities][0] : null;
    setTasks(prev => ([...prev, {
      id: `new-${Date.now()}`,
      city_id: cityId,
      set_date: '',
      initiator: '',
      task: '',
      responsible: '',
      participants: '',
      due_date: '',
      status: '',
      completion_date: '',
      delay_reason: '',
      comments: '',
      is_done: false,
      result: '',
      text_color: ''
    }]));
    showInfo('Добавлена новая строка');
  };

  const saveCell = async (taskId, field, value, additionalData = {}) => {
    const isNew = taskId.toString().startsWith('new-');
    if (isNew) {
      const updated = tasks.map(t => t.id === taskId ? { ...t, [field]: value, ...additionalData } : t);
      setTasks(updated);

      const row = updated.find(t => t.id === taskId);
      const minimalReady = row.task && String(row.task).trim().length > 0;
      if (minimalReady) {
        const payload = { ...row };
        delete payload.id;
        delete payload.isNew;

        payload.city_id = payload.city_id != null ? parseInt(payload.city_id) : null;

        Object.keys(payload).forEach((k) => {
          if (payload[k] === '') payload[k] = null;
          if (k === 'status' && (payload[k] === '' || payload[k] == null)) {
            delete payload[k];
          }
        });

        try {
          const res = await client.post('/project-office', payload);
          setTasks(prev => prev.map(t => t.id === taskId ? res.data : t));
          showSuccess('Задача создана');
        } catch (e) {
          console.error('Error creating task:', e);
          const errorMsg = e?.response?.data?.detail;
          if (typeof errorMsg === 'string') {
            showError(errorMsg);
          } else if (Array.isArray(errorMsg)) {
            showError(errorMsg.map(err => err.msg || err).join(', '));
          } else {
            showError('Ошибка сохранения');
          }
          fetchTasks();
        }
      }
      return;
    }

    let processed = value;
    if (typeof value === 'string') {
      processed = value.trim() === '' ? null : value.trim();
    }

    const updatePayload = { ...additionalData };

    if (field === 'status') {
      if (processed === null || processed === '') {
        updatePayload[field] = null;
      } else {
        updatePayload[field] = processed;
      }
    } else {
      updatePayload[field] = processed;
    }

    try {
      const res = await client.put(`/project-office/${taskId}`, updatePayload);
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...res.data } : t));
      if (field === 'status') {
        await fetchTasks();
      }
    } catch (e) {
      console.error('Error updating task:', e);
      const errorMsg = e?.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        showError(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        showError(errorMsg.map(err => err.msg || err).join(', '));
      } else {
        showError('Ошибка сохранения');
      }
      fetchTasks();
    }
  };

  const onTextareaKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.stopPropagation();
    }
  };

  const removeRow = async (id) => {
    if (!window.confirm('Удалить строку?')) return;
    if (id.toString().startsWith('new-')) {
      setTasks(prev => prev.filter(t => t.id !== id));
      showSuccess('Строка удалена');
      return;
    }
    try {
      await client.delete(`/project-office/${id}`);
      showSuccess('Задача удалена');
      fetchTasks();
    } catch (e) {
      console.error(e);
      showError('Ошибка при удалении');
    }
  };

  const renderEditable = (task, field, type = 'text') => {
    const isDate = ['set_date', 'completion_date'].includes(field);
    const valueForInput = (() => {
      const val = task[field];
      if (isDate && val) return formatDateForInput(val);
      return val ?? '';
    })();

    const displayValue = (() => {
      const v = task[field];
      if (isDate && v) {
        const iso = formatDateForInput(v);
        if (iso) {
          const [y, m, d] = iso.split('-').map(Number);
          return new Date(y, m - 1, d).toLocaleDateString('ru-RU');
        }
        return new Date(v).toLocaleDateString('ru-RU');
      }
      if (field === 'status' && v) return v;
      return v ?? '—';
    })();

    if (!projectOfficeCanEdit) {
      return <span style={{ whiteSpace: 'pre-wrap' }}>{displayValue}</span>;
    }

    if (field === 'status') {
      return (
        <select
          value={task.status || ''}
          onChange={(e) => setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: e.target.value } : t))}
          onBlur={(e) => saveCell(task.id, 'status', e.target.value)}
          style={{ width: '100%', color: task.status ? STATUS_COLORS[task.status] : undefined, fontWeight: 600, background: 'transparent' }}
        >
          <option value="">—</option>
          {Object.keys(STATUS_COLORS).map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      );
    }

    if (field === 'is_done') {
      return (
        <input type="checkbox" checked={!!task.is_done} onChange={() => toggleDone(task)} />
      );
    }

    if (field === 'comments') {
      const isExpanded = !!expandedComments[task.id];
      const textValue = task.comments || '';
      const needsToggle = textValue.length > 120 || (textValue.match(/\n/g)?.length || 0) >= 6;
      return (
        <div>
          <textarea
            value={textValue}
            placeholder="Комментарии"
            onChange={(e) => {
              const el = e.target;
              el.style.height = 'auto';
              el.style.height = `${el.scrollHeight}px`;
              setTasks(prev => prev.map(t => t.id === task.id ? { ...t, comments: e.target.value } : t));
            }}
            onBlur={(e) => saveCell(task.id, 'comments', e.target.value)}
            onKeyDown={onTextareaKeyDown}
            data-auto-resize={isExpanded ? 'true' : 'false'}
            style={{ width: '100%', resize: 'none', overflow: 'hidden', lineHeight: '1.4', maxHeight: isExpanded ? 'none' : '120px' }}
            rows={isExpanded ? 1 : 6}
          />
          {needsToggle && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setExpandedComments(prev => ({ ...prev, [task.id]: !isExpanded }))}
              style={{ marginTop: 4, padding: '2px 6px' }}
            >
              {isExpanded ? 'Свернуть' : 'Показать полностью'}
            </button>
          )}
        </div>
      );
    }

    if (field === 'delay_reason' || field === 'task') {
      const currentValue = field === 'delay_reason' ? (task.delay_reason || '') : (task.task || '');
      return (
        <textarea
          value={currentValue}
          placeholder={field === 'task' ? 'Задача' : 'Причина переноса срока'}
          onChange={(e) => {
            const el = e.target;
            el.style.height = 'auto';
            el.style.height = `${el.scrollHeight}px`;
            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, [field]: e.target.value } : t));
          }}
          onBlur={(e) => saveCell(task.id, field, e.target.value)}
          onKeyDown={onTextareaKeyDown}
          data-auto-resize="true"
          style={{ width: '100%', resize: 'none', overflow: 'hidden', lineHeight: '1.4' }}
          rows={6}
        />
      );
    }

    if (field === 'due_date') {
      return (
        <textarea
          value={task.due_date || ''}
          placeholder="Срок"
          onChange={(e) => {
            const el = e.target;
            el.style.height = 'auto';
            el.style.height = `${el.scrollHeight}px`;
            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, due_date: e.target.value } : t));
          }}
          onBlur={(e) => saveCell(task.id, 'due_date', e.target.value)}
          onKeyDown={onTextareaKeyDown}
          data-auto-resize="true"
          style={{ width: '100%', resize: 'none', overflow: 'hidden', lineHeight: '1.4' }}
          rows={6}
        />
      );
    }

    return (
      <input
        type={isDate ? 'date' : type}
        value={valueForInput}
        placeholder={
          field === 'initiator' ? 'Постановщик' :
          field === 'responsible' ? 'Ответственный' :
          field === 'participants' ? 'Участники' :
          field === 'result' ? 'Результат работы' : ''
        }
        onChange={(e) => setTasks(prev => prev.map(t => t.id === task.id ? { ...t, [field]: e.target.value } : t))}
        onBlur={(e) => saveCell(task.id, field, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            saveCell(task.id, field, e.target.value);
          }
        }}
        style={{ width: '100%' }}
      />
    );
  };

  const toggleDone = async (task) => {
    const newVal = !task.is_done;
    try {
      if (task.id.toString().startsWith('new-')) {
        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, is_done: newVal } : t));
      } else {
        await client.put(`/project-office/${task.id}`, { is_done: newVal });
        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, is_done: newVal } : t));
        showSuccess(newVal ? 'Задача отмечена как выполненная' : 'Отметка снята');
      }
    } catch (e) {
      console.error(e);
      showError('Ошибка при обновлении');
    }
  };

  return (
    <div className="container-fluid">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap' }}>
        <h1 style={{ marginBottom: 0 }}>Проектный офис</h1>
        <span style={{ color: 'var(--text-muted)' }}>управление задачами по объектам</span>
      </div>

      <div className="po-tabs" role="tablist" aria-label="Разделы проектного офиса">
        <button
          type="button"
          role="tab"
          aria-selected={viewTab === 'active'}
          className={`po-tabs__btn${viewTab === 'active' ? ' po-tabs__btn--active' : ''}`}
          onClick={() => setViewTab('active')}
        >
          Активные
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={viewTab === 'archive'}
          className={`po-tabs__btn${viewTab === 'archive' ? ' po-tabs__btn--active' : ''}`}
          onClick={() => setViewTab('archive')}
        >
          Архив
        </button>
      </div>

      <div className="po-toolbar" style={{ borderBottom: '2px solid var(--border-color)', marginBottom: 16 }}>
        <div className="po-toolbar__row" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', paddingBottom: 12 }}>
          <ProjectOfficeCitySelect
            cities={cities}
            selectedCities={selectedCities}
            includeCommon={includeCommon}
            onChangeCities={handleChangeCities}
            onToggleCommon={handleToggleCommon}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={formatSortValue(sortBy, sortOrder)}
              onChange={(e) => {
                const { field, order } = parseSortValue(e.target.value);
                setSortBy(field);
                setSortOrder(order);
              }}
              className="form-control"
              style={{ minWidth: 200, maxWidth: 220 }}
            >
              <option value="set_date_asc">↗ Дата постановки (возр.)</option>
              <option value="set_date_desc">↘ Дата постановки (убыв.)</option>
              <option value="initiator_asc">↗ Постановщик (А→Я)</option>
              <option value="initiator_desc">↘ Постановщик (Я→А)</option>
              <option value="responsible_asc">↗ Ответственный (А→Я)</option>
              <option value="responsible_desc">↘ Ответственный (Я→А)</option>
              <option value="due_date_asc">↗ Срок (А→Я)</option>
              <option value="due_date_desc">↘ Срок (Я→А)</option>
              <option value="status_asc">↗ Статус (А→Я)</option>
              <option value="status_desc">↘ Статус (Я→А)</option>
            </select>
            {projectOfficeCanEdit && viewTab === 'active' && (
              <button type="button" onClick={addRow} className="btn btn-primary">+ Добавить строку</button>
            )}
          </div>
        </div>

        <div className="po-filters">
          <div className="po-filters__group">
            <span className="po-filters__label">Дата постановки</span>
            <input
              type="date"
              className="form-control po-filters__date"
              value={setDateFrom}
              onChange={(e) => setSetDateFrom(e.target.value)}
              title="С"
            />
            <span className="po-filters__sep">—</span>
            <input
              type="date"
              className="form-control po-filters__date"
              value={setDateTo}
              onChange={(e) => setSetDateTo(e.target.value)}
              title="По"
            />
          </div>

          <ProjectOfficeFilterMultiSelect
            label="Ответственный"
            options={responsibleOptions}
            selected={responsibleFilter}
            onChange={setResponsibleFilter}
            minWidth={180}
          />

          <ProjectOfficeFilterMultiSelect
            label="Статус"
            options={statusOptions}
            selected={statusFilter}
            onChange={setStatusFilter}
            minWidth={150}
          />

          <input
            type="text"
            className="form-control"
            placeholder="Поиск по дате (дд.мм.гггг)"
            value={dateSearch}
            onChange={(e) => setDateSearch(e.target.value)}
            style={{ minWidth: 180, maxWidth: 220 }}
          />

          <input
            type="text"
            placeholder="Поиск: задача, ответственный, участники…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="form-control"
            style={{ minWidth: 240, flex: 1 }}
          />

          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetFilters}>
            Сбросить фильтры
          </button>
        </div>
      </div>

      <div className="card-full-width po-card" style={{ padding: 0, overflow: 'hidden' }}>
        <SyncedHorizontalScroll
          data-testid="project-office-table-scroll"
          style={{ padding: 0 }}
          maxHeight="calc(100vh - 280px)"
          contentKey={`${filteredTasks.length}-${selectedCities.size}-${includeCommon ? 1 : 0}`}
        >
          <table className="table po-table" style={{ marginBottom: 0 }}>
          <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--table-stripe)', zIndex: 10, boxShadow: 'inset 0 -1px 0 var(--border-color)' }}>
            <tr>
              {(selectedCities.size > 1 || includeCommon) && (
                <th style={{ minWidth: '140px' }}>Объект</th>
              )}
              <th style={{ minWidth: '140px' }}>Дата постановки</th>
              <th style={{ minWidth: '140px' }}>Постановщик</th>
              <th style={{ minWidth: '260px' }}>Задача</th>
              <th style={{ minWidth: '160px' }}>Ответственный</th>
              <th style={{ minWidth: '200px' }}>Участники</th>
              <th style={{ minWidth: '140px' }}>Срок</th>
              <th style={{ minWidth: '140px' }}>Статус</th>
              <th style={{ minWidth: '140px' }}>Дата выполнения</th>
              <th style={{ minWidth: '180px' }}>Причина переноса срока</th>
              <th style={{ minWidth: '360px' }}>Комментарии</th>
              <th style={{ minWidth: '220px' }}>Результат работы</th>
              <th style={{ width: '80px' }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredTasks.map((t, idx) => (
              <tr key={t.id} className={t.is_done ? 'po-row-done' : ''} style={{ background: idx % 2 === 0 ? 'var(--bg-card)' : 'var(--table-stripe)' }}>
                {(selectedCities.size > 1 || includeCommon) && (
                  <td style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                    {t.city_id ? (cities.find(c => c.id === t.city_id)?.name || '—') : 'Общие'}
                  </td>
                )}
                <td>
                  {renderEditable(t, 'set_date')}
                </td>
                <td>{renderEditable(t, 'initiator')}</td>
                <td>{renderEditable(t, 'task')}</td>
                <td>{renderEditable(t, 'responsible')}</td>
                <td>{renderEditable(t, 'participants')}</td>
                <td>{renderEditable(t, 'due_date')}</td>
                <td style={getStatusCellStyle(t.status)}>{renderEditable(t, 'status')}</td>
                <td>{renderEditable(t, 'completion_date')}</td>
                <td>{renderEditable(t, 'delay_reason')}</td>
                <td>{renderEditable(t, 'comments')}</td>
                <td>{renderEditable(t, 'result')}</td>
                <td>
                  {projectOfficeCanEdit && (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm po-delete-btn"
                      title="Удалить строку"
                      onClick={() => removeRow(t.id)}
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filteredTasks.length === 0 && (
              <tr>
                <td colSpan={(selectedCities.size > 1 || includeCommon) ? 13 : 12} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
                  {viewTab === 'archive'
                    ? (selectedCities.size > 0 || includeCommon
                      ? 'В архиве пока нет задач'
                      : 'Выберите объект(ы) или «Общие задачи»')
                    : `Нет данных. Измените критерии поиска${projectOfficeCanEdit ? ' или нажмите «Добавить строку»' : ''}.`}
                </td>
              </tr>
            )}
          </tbody>
          </table>
        </SyncedHorizontalScroll>
      </div>
    </div>
  );
};

export default ProjectOffice;
